import { Mesh, PlaneGeometry, Vector3 } from 'three';
import { Ability } from './Ability.js';
import { settings } from '../config/settings.js';
import { ParticleShape } from '../particles/ParticleSystem.js';
import { RateEmitter } from '../particles/ParticleEngine.js';
import { frame } from '../core/FrameUniforms.js';
import { LAYER } from '../core/Layers.js';
import { Easing, saturate } from '../utils/math.js';
import { getColor } from '../utils/color.js';
import {
  createRunicGlyphMaterial,
  syncRunicGlyphMaterial
} from '../materials/RunicGlyphMaterial.js';

const _pos = new Vector3();
const _dir = new Vector3();
const _up = new Vector3(0, 1, 0);
const _emit = {};

/**
 * The first geometric/rune prototype: intentionally visual-only. It erects an
 * original three-layer arcane diagram in front of the caster, performs no
 * impact action, then dissolves into sparks.
 */
export class RunicAegisAbility extends Ability {
  constructor(context) {
    super('runic', context);
  }

  createShaders() {
    this.glyphGeometry = new PlaneGeometry(2, 2);
    this.layers = [];
    for (let index = 0; index < 3; index++) {
      const material = createRunicGlyphMaterial(index);
      const mesh = new Mesh(this.glyphGeometry, material);
      mesh.layers.set(LAYER.VFX);
      mesh.renderOrder = 14 + index;
      mesh.frustumCulled = false;
      this.group.add(mesh);
      this.layers.push({ mesh, material });
    }
    this.centre = new Vector3();
    this._seed = 0;
  }

  createParticles() {
    this.sparks = this.ctx.particles.get('runic.sparks', {
      capacity: 2200,
      shape: ParticleShape.STREAK,
      additive: true,
      curl: true,
      stretch: true,
      swirl: true,
      softFade: 0.45
    });
    this.motes = this.ctx.particles.get('runic.motes', {
      capacity: 1400,
      shape: ParticleShape.SOFT,
      additive: true,
      curl: true,
      swirl: true,
      softFade: 0.55
    });
    this.sparkEmitter = new RateEmitter();
    this.moteEmitter = new RateEmitter();
  }

  get impactDuration() {
    return settings.runic.holdTime * settings.global.lifetime;
  }

  get fadeDuration() {
    return settings.runic.fadeTime * settings.global.lifetime;
  }

  get instanceCount() {
    return this.layers.length;
  }

  onSpawn() {
    this._seed = Math.random() * 10;
    this.sparkEmitter.reset();
    this.moteEmitter.reset();
    for (const layer of this.layers) layer.mesh.visible = true;
    this._placeGlyph();
    this._sync(0, 1);
  }

  _placeGlyph() {
    const c = settings.runic;
    this.centre.copy(this.origin).addScaledVector(this.direction, c.forwardOffset).setY(c.height);
    this.position.copy(this.centre);
    const yaw = Math.atan2(this.direction.x, this.direction.z);
    for (let index = 0; index < this.layers.length; index++) {
      const layer = this.layers[index];
      layer.mesh.position.copy(this.centre).addScaledVector(this.direction, index * 0.018);
      layer.mesh.rotation.set(0, yaw, 0);
      layer.mesh.scale.setScalar(c.radius * (1 - index * 0.055));
    }
  }

  _sync(reveal, fade) {
    const c = settings.runic;
    for (let index = 0; index < this.layers.length; index++) {
      const sign = index === 1 ? -1 : 1;
      syncRunicGlyphMaterial(this.layers[index].material, c, {
        reveal: saturate(reveal - index * 0.09),
        fade,
        rotation: this.age * c.rotationSpeed * sign * (1 + index * 0.42) + this._seed
      });
    }
  }

  _syncParticles(dt, strength) {
    const c = settings.runic;
    this.sparks.setGradient(getColor(c.colorWhite), getColor(c.colorGold), getColor(c.colorOrange), getColor(c.colorEmber));
    this.motes.setGradient(getColor(c.colorWhite), getColor(c.colorOrange), getColor(c.colorGold), getColor(c.colorEmber));
    this.sparks.uniforms.uSwirl.value = 9;
    this.sparks.uniforms.uTurbulence.value = 1.1;
    this.motes.uniforms.uSwirl.value = 5;

    const angle = this.age * c.rotationSpeed * 2.2;
    _pos.copy(this.centre)
      .addScaledVector(this.side, Math.cos(angle) * c.radius * 0.82)
      .addScaledVector(_up, Math.sin(angle) * c.radius * 0.82);
    _dir.copy(this.side).multiplyScalar(-Math.sin(angle))
      .addScaledVector(_up, Math.cos(angle)).normalize();
    this._emit(this.sparks, this.sparkEmitter.tick(dt, c.sparkRate * strength), _pos, _dir, c.sparkSpeed, c.sparkSize, 0.62);
    this._emit(this.motes, this.moteEmitter.tick(dt, c.moteRate * strength), this.centre, _dir, 1.1, c.moteSize, 1.0);
  }

  _emit(system, count, position, direction, speed, size, life) {
    if (count <= 0) return;
    _emit.position = position;
    _emit.radius = settings.runic.radius * 0.16;
    _emit.direction = direction;
    _emit.speed = speed * settings.global.particleSpeed;
    _emit.speedVariance = 0.7;
    _emit.spread = 0.75;
    _emit.inherit = null;
    _emit.anchor = this.centre;
    _emit.size = size * settings.global.particleSize;
    _emit.sizeVariance = 0.6;
    _emit.life = life * settings.global.particleLifetime;
    _emit.lifeVariance = 0.4;
    _emit.spin = 8;
    _emit.tint = null;
    _emit.time = frame.uTime.value;
    system.emit(Math.round(count * settings.global.particleCount), _emit);
  }

  onTravel(dt) {
    // The glyph is already in front of the caster while the one-frame delivery
    // enters the shared impact/fade lifecycle.
    const reveal = Easing.outCubic(saturate(this.age / settings.runic.revealTime));
    this._sync(reveal, 1);
    this._syncParticles(dt, reveal);
  }

  onImpact() {
    // Deliberately no damage, decal, burst, status or gameplay callback.
    this.position.copy(this.centre);
    this.lightBoost = settings.runic.arrivalLight;
  }

  onFade(dt, t) {
    const fade = t <= 1 ? 1 : Math.max(0, 2 - t);
    const reveal = Easing.outCubic(saturate((this.age + this.impactTime) / settings.runic.revealTime));
    this._sync(reveal, fade);
    this._syncParticles(dt, fade * (0.65 + 0.35 * Math.sin(this.age * 3.2) ** 2));
    this.ctx.shake.rumble(settings.runic.rumble * fade * settings.global.cameraShake, dt);
  }

  lightShimmer() {
    return 0.86 + 0.14 * Math.sin(this.age * 11.0) * Math.sin(this.age * 3.7);
  }

  onDestroy() {
    for (const layer of this.layers) layer.mesh.visible = false;
    this.sparkEmitter.reset();
    this.moteEmitter.reset();
  }

  dispose() {
    this.glyphGeometry.dispose();
    for (const layer of this.layers) layer.material.dispose();
    super.dispose();
  }
}
